import React, { useState, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useResetPassword } from '../../hooks/auth/usePasswordReset';
import { NotificationBanner } from '../../components/NotificationBanner';
import { Lock, Loader2, CheckCircle, Eye, EyeOff } from 'lucide-react';

export const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const { mutate: resetPassword, isLoading, isSuccess, error } = useResetPassword();

  const passwordValidation = useMemo(() => {
    return {
      minLength: newPassword.length >= 8,
      hasUppercase: /[A-Z]/.test(newPassword),
      hasLowercase: /[a-z]/.test(newPassword),
      hasNumber: /[0-9]/.test(newPassword),
    };
  }, [newPassword]);

  const isPasswordValid = Object.values(passwordValidation).every(Boolean);
  const passwordsMatch = newPassword === confirmPassword && newPassword.length > 0;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    if (!token) {
      setLocalError('Token de réinitialisation manquant ou invalide');
      return;
    }

    if (!isPasswordValid) {
      setLocalError('Le mot de passe ne respecte pas tous les critères');
      return;
    }

    if (!passwordsMatch) {
      setLocalError('Les mots de passe ne correspondent pas');
      return;
    }

    resetPassword(
      { token, newPassword },
      {
        onSuccess: () => {
          setTimeout(() => {
            navigate('/login');
          }, 3000);
        },
      },
    );
  };

  const displayError = error || localError;

  if (!token) {
    return (
      <div className="tw-min-h-screen tw-flex tw-items-center tw-justify-center tw-bg-gradient-to-br tw-from-affinicar-secondary tw-to-affinicar-dark tw-p-4">
        <div className="tw-w-full tw-max-w-md tw-bg-white tw-rounded-2xl tw-shadow-2xl tw-p-8 tw-text-center">
          <div className="tw-text-red-600 tw-text-xl tw-font-semibold tw-mb-4">
            Lien invalide
          </div>
          <p className="tw-text-gray-600 tw-mb-6">
            Le lien de réinitialisation est manquant ou invalide.
          </p>
          <Link
            to="/forgot-password"
            className="tw-inline-block tw-py-3 tw-px-6 tw-bg-affinicar-primary hover:tw-bg-affinicar-secondary tw-text-white tw-font-semibold tw-rounded-lg tw-transition-colors"
          >
            Demander un nouveau lien
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="tw-min-h-screen tw-flex tw-items-center tw-justify-center tw-bg-gradient-to-br tw-from-affinicar-secondary tw-to-affinicar-dark tw-p-4">
      <div className="tw-w-full tw-max-w-md">
        {/* Card */}
        <div className="tw-bg-white tw-rounded-2xl tw-shadow-2xl tw-overflow-hidden">
          {/* Header */}
          <div className="tw-bg-gradient-to-r tw-from-affinicar-primary tw-to-affinicar-secondary tw-p-8 tw-text-center">
            <div className="tw-mx-auto tw-mb-4">
              <img
                src="/assets/images/affinicar/logo_full_white.png"
                alt="Affinicar"
                className="tw-h-16 tw-w-auto tw-mx-auto"
              />
            </div>
            <h1 className="tw-text-2xl tw-font-bold tw-text-white tw-mb-2">
              Nouveau mot de passe
            </h1>
            <p className="tw-text-white tw-text-opacity-90">
              Créez un nouveau mot de passe sécurisé
            </p>
          </div>

          {/* Content */}
          <div className="tw-p-8">
            {displayError && (
              <div className="tw-mb-6">
                <NotificationBanner
                  message={
                    (error as any)?.response?.data?.message ||
                    localError ||
                    'Une erreur est survenue'
                  }
                  type="error"
                />
              </div>
            )}

            {isSuccess ? (
              <div className="tw-space-y-6">
                <div className="tw-flex tw-flex-col tw-items-center tw-text-center">
                  <div className="tw-w-16 tw-h-16 tw-bg-green-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mb-4">
                    <CheckCircle className="tw-w-8 tw-h-8 tw-text-green-600" />
                  </div>
                  <h2 className="tw-text-xl tw-font-semibold tw-text-gray-900 tw-mb-2">
                    Mot de passe réinitialisé
                  </h2>
                  <p className="tw-text-gray-600 tw-mb-2">
                    Votre mot de passe a été réinitialisé avec succès.
                  </p>
                  <p className="tw-text-sm tw-text-gray-500">
                    Vous allez être redirigé vers la page de connexion...
                  </p>
                </div>

                <Link
                  to="/login"
                  className="tw-block tw-w-full tw-text-center tw-py-3 tw-px-4 tw-bg-affinicar-primary hover:tw-bg-affinicar-secondary tw-text-white tw-font-semibold tw-rounded-lg tw-transition-colors tw-duration-200"
                >
                  Se connecter
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="tw-space-y-6">
                {/* Nouveau mot de passe */}
                <div>
                  <label
                    htmlFor="newPassword"
                    className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2"
                  >
                    Nouveau mot de passe
                  </label>
                  <div className="tw-relative">
                    <Lock className="tw-absolute tw-left-3 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-w-5 tw-h-5 tw-text-gray-400" />
                    <input
                      id="newPassword"
                      type={showPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => {
                        setNewPassword(e.target.value);
                        setLocalError(null);
                      }}
                      className="tw-w-full tw-pl-10 tw-pr-12 tw-py-3 tw-border tw-border-gray-300 tw-rounded-lg focus:tw-ring-2 focus:tw-ring-affinicar-primary focus:tw-border-transparent tw-transition-all"
                      placeholder="Nouveau mot de passe"
                      required
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="tw-absolute tw-right-3 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-text-gray-400 hover:tw-text-gray-600"
                    >
                      {showPassword ? <EyeOff className="tw-w-5 tw-h-5" /> : <Eye className="tw-w-5 tw-h-5" />}
                    </button>
                  </div>

                  {/* Validation du mot de passe */}
                  {newPassword && (
                    <div className="tw-mt-3 tw-space-y-2">
                      <p className="tw-text-xs tw-text-gray-600 tw-font-medium">
                        Le mot de passe doit contenir :
                      </p>
                      <ul className="tw-space-y-1">
                        <li
                          className={`tw-text-xs tw-flex tw-items-center ${
                            passwordValidation.minLength ? 'tw-text-green-600' : 'tw-text-gray-500'
                          }`}
                        >
                          <span className="tw-mr-2">{passwordValidation.minLength ? '✓' : '○'}</span>
                          Au moins 8 caractères
                        </li>
                        <li
                          className={`tw-text-xs tw-flex tw-items-center ${
                            passwordValidation.hasUppercase ? 'tw-text-green-600' : 'tw-text-gray-500'
                          }`}
                        >
                          <span className="tw-mr-2">{passwordValidation.hasUppercase ? '✓' : '○'}</span>
                          Une majuscule
                        </li>
                        <li
                          className={`tw-text-xs tw-flex tw-items-center ${
                            passwordValidation.hasLowercase ? 'tw-text-green-600' : 'tw-text-gray-500'
                          }`}
                        >
                          <span className="tw-mr-2">{passwordValidation.hasLowercase ? '✓' : '○'}</span>
                          Une minuscule
                        </li>
                        <li
                          className={`tw-text-xs tw-flex tw-items-center ${
                            passwordValidation.hasNumber ? 'tw-text-green-600' : 'tw-text-gray-500'
                          }`}
                        >
                          <span className="tw-mr-2">{passwordValidation.hasNumber ? '✓' : '○'}</span>
                          Un chiffre
                        </li>
                      </ul>
                    </div>
                  )}
                </div>

                {/* Confirmation */}
                <div>
                  <label
                    htmlFor="confirmPassword"
                    className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2"
                  >
                    Confirmer le mot de passe
                  </label>
                  <div className="tw-relative">
                    <Lock className="tw-absolute tw-left-3 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-w-5 tw-h-5 tw-text-gray-400" />
                    <input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        setLocalError(null);
                      }}
                      className="tw-w-full tw-pl-10 tw-pr-12 tw-py-3 tw-border tw-border-gray-300 tw-rounded-lg focus:tw-ring-2 focus:tw-ring-affinicar-primary focus:tw-border-transparent tw-transition-all"
                      placeholder="Confirmer le mot de passe"
                      required
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="tw-absolute tw-right-3 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-text-gray-400 hover:tw-text-gray-600"
                    >
                      {showConfirmPassword ? <EyeOff className="tw-w-5 tw-h-5" /> : <Eye className="tw-w-5 tw-h-5" />}
                    </button>
                  </div>

                  {confirmPassword && (
                    <p
                      className={`tw-text-xs tw-mt-2 ${
                        passwordsMatch ? 'tw-text-green-600' : 'tw-text-red-600'
                      }`}
                    >
                      {passwordsMatch ? '✓ Les mots de passe correspondent' : '✗ Les mots de passe ne correspondent pas'}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading || !isPasswordValid || !passwordsMatch}
                  className="tw-w-full tw-py-3 tw-px-4 tw-bg-affinicar-primary hover:tw-bg-affinicar-secondary tw-text-white tw-font-semibold tw-rounded-lg tw-transition-colors tw-duration-200 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-flex tw-items-center tw-justify-center"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="tw-animate-spin tw-w-5 tw-h-5 tw-mr-2" />
                      Réinitialisation...
                    </>
                  ) : (
                    'Réinitialiser le mot de passe'
                  )}
                </button>

                <div className="tw-text-center">
                  <Link
                    to="/login"
                    className="tw-text-sm tw-text-affinicar-primary hover:tw-text-affinicar-secondary tw-font-medium"
                  >
                    Retour à la connexion
                  </Link>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
